import React, { useEffect } from "react";
import { ArrowRight } from "lucide-react";

import { cn } from "../lib/utils";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";
import {
  emitZar8YieldOverviewEvent,
  formatZar8YieldOverviewDate,
} from "../utils/zar8YieldOverview";
import Zar8YieldOverviewChart from "./Zar8YieldOverviewChart";
const HERO_BACKGROUND = "/figma/zar8-yield/hero-background.svg";
const TRACK_ICON = "/figma/zar8-yield/track-icon.svg";
const CALCULATOR_ICON = "/figma/zar8-yield/calculator-icon.svg";
const MANAGE_ICON_PART = "/figma/zar8-yield/manage-icon-part-1.svg";
const BTC_ICON = "/dpos/figma/icons/crypto-btc.svg";
const ZAR8_MARK_SMALL = "/figma/zar8-yield/zar8-mark-small.png";

const CTA_ARROW_ICON = "/figma/zar8-yield/cta-arrow.svg";
const NOISE_TEXTURE = "/figma/zar8-yield/noise.png";

function ManageGlyph() {
  return (
    <span className="relative block h-6 w-6">
      <img
        src={MANAGE_ICON_PART}
        alt=""
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[17px] w-[1px] -translate-x-1/2 -translate-y-1/2"
      />
      <img
        src={MANAGE_ICON_PART}
        alt=""
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[17px] w-[1px] -translate-x-1/2 -translate-y-1/2 rotate-90"
      />
    </span>
  );
}

function SummaryActionButton({
  icon,
  label,
  onClick,
  isActive = false,
  disabled = false,
}) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      disabled={disabled || isActive}
      onClick={isActive ? undefined : onClick}
      className={cn(
        "flex h-[72px] items-center justify-between rounded-2xl border border-[#ffffff54] p-4 text-left text-white backdrop-blur-[16px] transition-all",
        isActive
          ? "bg-[rgba(255,255,255,0.14)] opacity-[0.2]"
          : "hover:text-white/50",
        disabled && !isActive && "cursor-not-allowed opacity-50"
      )}
      style={{
        background:
          "radial-gradient(196.29% 458.78% at -8.86% -12.86%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)",
      }}
    >
      <span className="flex min-w-0 flex-1 items-center gap-4">
        <span className="flex h-8 w-8 items-center justify-center">{icon}</span>
        <span className="w-[202px] text-base font-semibold leading-6">
          {label}
        </span>
      </span>

      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-accent/30 bg-transparent">
        <img
          src={CTA_ARROW_ICON}
          alt=""
          aria-hidden="true"
          className="h-3 w-3"
        />
      </span>
    </button>
  );
}

function SummaryCard({
  totalYieldValue,
  onManage,
  onCalculator,
  isPreview = false,
}) {
  return (
    <section
      className={cn(
        "relative min-h-[327px] overflow-hidden rounded-[20px] text-white",
        isPreview ? "xl:w-[752px]" : "xl:w-auto"
      )}
      style={{
        background:
          "linear-gradient(90deg, rgba(87, 36, 156, 0.5) 0%, rgba(87, 36, 156, 0.5) 100%), linear-gradient(90deg, rgb(95, 117, 255) 0%, rgb(151, 71, 255) 100%)",
      }}
    >
      <img
        src={HERO_BACKGROUND}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-[-120px] top-[-55px] max-w-none"
        style={{
          width: "1803.565px",
          height: "1379.698px",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-40"
        style={{
          backgroundImage: `url('${NOISE_TEXTURE}')`,
          backgroundPosition: "top left",
          backgroundSize: "1697.338px 846.685px",
        }}
      />

      <div className="relative flex min-h-[327px] flex-col px-5 pb-5 pt-[34px] xl:block">
        <p className="text-2xl font-semibold leading-7 text-[#F1EDF7] xl:absolute xl:left-5 xl:top-[34px]">
          ZAR8/BTC Yield
        </p>

        <div className="mt-[67px] space-y-[13px] xl:absolute xl:left-[23px] xl:top-[137px] xl:mt-0 xl:w-[358px]">
          <p className="text-base leading-6 text-[rgba(235,233,239,0.6)]">
            Total ZAR8
          </p>
          <p className="text-[48px] font-semibold leading-[40px] text-white">
            {formatFiatWithTwoDecimals(totalYieldValue)}
          </p>
        </div>

        <div className="mt-10 grid gap-[14px] lg:grid-cols-3 xl:absolute xl:bottom-5 xl:left-[23px] xl:right-[23px] xl:mt-0">
          <SummaryActionButton
            label="Manage"
            onClick={onManage}
            disabled={isPreview}
            icon={<ManageGlyph />}
          />
          <SummaryActionButton
            label="Track"
            isActive
            icon={
              <img
                src={TRACK_ICON}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 object-contain"
              />
            }
          />
          <SummaryActionButton
            label="Calculator"
            onClick={onCalculator}
            disabled={isPreview}
            icon={
              <img
                src={CALCULATOR_ICON}
                alt=""
                aria-hidden="true"
                className="h-8 w-8 object-contain"
              />
            }
          />
        </div>
      </div>
    </section>
  );
}

function AssetTag({ symbol }) {
  return (
    <span className="inline-flex items-center gap-[3px] rounded-[50px] bg-[rgba(235,233,239,0.3)] p-[8px]">
      <img
        src={ZAR8_MARK_SMALL}
        alt=""
        aria-hidden="true"
        className="h-3 w-3 object-contain"
      />
      <span className="text-[12px] font-semibold leading-7 text-[#423E51]">
        {symbol}
      </span>
    </span>
  );
}

function PositionCoin({ title }) {
  const isGold = title.includes("12");

  if (isGold) {
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[linear-gradient(180deg,#EFD58F_0%,#D0A842_100%)] text-sm font-bold text-white">
        T
      </span>
    );
  }

  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F7931A]">
      <img
        src={BTC_ICON}
        alt=""
        aria-hidden="true"
        className="h-4 w-4 object-contain"
      />
    </span>
  );
}

function PositionDetail({ label, value }) {
  return (
    <div>
      <p className="text-sm leading-5 text-[#6D6584]">{label}</p>
      <p className="mt-3 text-sm font-semibold leading-5 text-[#423E51]">
        {value}
      </p>
    </div>
  );
}

function PositionCard({
  position,
  onUnlock,
  onViewPerformance,
  isPreview = false,
}) {
  return (
    <article
      className={cn(
        "flex min-h-[271px] flex-col justify-between rounded-[16px] border border-[#EBE9EF] bg-white px-4 py-[19px] transition-colors",
        !isPreview && position.productId
          ? "cursor-pointer hover:bg-[#FAF9FC]"
          : ""
      )}
      onClick={() => {
        if (!isPreview && position.productId) {
          onViewPerformance?.(position);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();

          if (!isPreview && position.productId) {
            onViewPerformance?.(position);
          }
        }
      }}
      role={!isPreview && position.productId ? "button" : undefined}
      tabIndex={!isPreview && position.productId ? 0 : undefined}
    >
      <div className="flex w-full flex-1 flex-col">
        <div className="flex w-full items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <PositionCoin title={position.title} />
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[20px] font-semibold leading-7 text-[#423E51]">
                {position.title}
              </h3>
              <AssetTag symbol={position.assetSymbol} />
            </div>
          </div>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onViewPerformance?.(position);
            }}
            disabled={isPreview || !position.productId}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F1EDF7] text-[#9D96AF] transition-colors hover:bg-[#FAF9FC] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={`View ${position.title} performance`}
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-[56px] grid w-full grid-cols-3 gap-6 pb-[19px] xl:gap-10">
          <PositionDetail
            label="Locked Amount"
            value={formatFiatWithTwoDecimals(position.lockedAmount)}
          />
          <PositionDetail
            label="Start date"
            value={formatZar8YieldOverviewDate(position.startDate)}
          />
          <PositionDetail
            label="Maturity Date"
            value={formatZar8YieldOverviewDate(position.maturityDate)}
          />
        </div>
      </div>

      <div className="mt-3 flex w-full gap-2">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onUnlock?.(position);
          }}
          disabled={isPreview}
          className="flex h-12 flex-1 items-center justify-center rounded-full text-sm font-semibold leading-5 text-[#EB3366] transition-colors hover:bg-[#FFF5F8] disabled:cursor-not-allowed disabled:opacity-40"
        >
          Unlock
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onViewPerformance?.(position);
          }}
          disabled={isPreview || !position.productId}
          className="flex h-12 flex-1 items-center justify-center rounded-full border border-[#F1EDF7] text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FAF9FC] disabled:cursor-not-allowed disabled:opacity-40"
        >
          View performance
        </button>
      </div>
    </article>
  );
}

function ActivePositionsSection({
  positions,
  hasError = false,
  onManage,
  onUnlock,
  onViewPerformance,
  isPreview = false,
}) {
  return (
    <section>
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-[20px] font-semibold leading-7 text-[#423E51]">
          Active
        </h2>
      </div>

      {hasError ? (
        <div className="mt-6 rounded-[20px] border border-dashed border-[#EBE9EF] bg-white px-6 py-10 text-center">
          <p className="text-lg font-semibold leading-6 text-[#423E51]">
            Active positions unavailable
          </p>
          <p className="mt-2 text-sm leading-5 text-[#6D6584]">
            We couldn’t load your active yield positions right now.
          </p>
        </div>
      ) : null}

      {!hasError && positions.length === 0 ? (
        <div className="mt-6 rounded-[20px] border border-dashed border-[#EBE9EF] bg-white px-6 py-10 text-center">
          <p className="text-lg font-semibold leading-6 text-[#423E51]">
            No active yield positions yet
          </p>
          <p className="mt-2 text-sm leading-5 text-[#6D6584]">
            Lock ZAR8 to start tracking performance and manage active yield
            positions here.
          </p>
          <button
            type="button"
            onClick={onManage}
            disabled={isPreview}
            className="mt-6 inline-flex h-12 items-center justify-center rounded-full bg-[#33125F] px-6 text-sm font-semibold leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Manage
          </button>
        </div>
      ) : null}

      {!hasError && positions.length > 0 ? (
        <div className="mt-[19px] grid grid-cols-1 gap-3 xl:grid-cols-3">
          {positions.map((position) => (
            <PositionCard
              key={position.id}
              position={position}
              onUnlock={onUnlock}
              onViewPerformance={onViewPerformance}
              isPreview={isPreview}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default function InvestYieldPopulatedState({
  totalYieldValue,
  totalLockedValue,
  positions = [],
  performanceSeries = [],
  isPerformanceLoading = false,
  hasPerformanceError = false,
  hasPositionsError = false,
  onManage,
  onCalculator,
  onViewMore,
  onUnlockPosition,
  onViewPositionPerformance,
  isPreview = false,
}) {
  useEffect(() => {
    emitZar8YieldOverviewEvent("zar8_yield_overview_viewed");
  }, []);

  const handleViewMore = () => {
    emitZar8YieldOverviewEvent("zar8_yield_overview_view_more_clicked", {
      positionsCount: positions.length,
    });
    onViewMore?.();
  };

  const handleUnlock = (position) => {
    emitZar8YieldOverviewEvent("zar8_yield_overview_unlock_clicked", {
      ledgerId: position.ledgerId,
      productId: position.productId,
      term: position.title,
    });
    onUnlockPosition?.(position);
  };

  const contentWidthClass = isPreview ? "w-[1304px]" : "w-full";
  const outerPaddingClass = isPreview
    ? "w-[1304px]"
    : "px-5 pb-12 pt-3 md:px-8 xl:px-[52px]";

  return (
    <div className={outerPaddingClass}>
      <div className={cn("flex flex-col gap-[37px]", contentWidthClass)}>
        <div
          className={cn(
            "grid gap-3",
            isPreview
              ? "xl:grid-cols-[752px_540px]"
              : "xl:grid-cols-[minmax(0,1.45fr)_minmax(420px,1fr)]"
          )}
        >
          <SummaryCard
            totalYieldValue={totalYieldValue}
            onManage={onManage}
            onCalculator={onCalculator}
            isPreview={isPreview}
          />
          <Zar8YieldOverviewChart
            series={performanceSeries}
            totalLockedValue={totalLockedValue}
            isLoading={isPerformanceLoading}
            hasError={hasPerformanceError}
            onViewMore={handleViewMore}
            viewMoreDisabled={isPreview || positions.length === 0}
            isPreview={isPreview}
          />
        </div>

        <ActivePositionsSection
          positions={positions}
          hasError={hasPositionsError}
          onManage={onManage}
          onUnlock={handleUnlock}
          onViewPerformance={onViewPositionPerformance}
          isPreview={isPreview}
        />
      </div>
    </div>
  );
}
